// Inbox's things as RowList rows (BL-189 N1): what each row says, in words, with no drawing of its own.
// What waits for a person's OK (approvals.list) and what the agents did (agents.activity); the views
// ApprovalList and AgentActivity draw them, and so can a page.
import type { Row, RowTone } from '../components/RowList/RowList.tsx';
import { agentName } from '../components/view-act.tsx';
import { clock } from '../format.ts';
import type { AgentActivityProps, ApprovalListProps } from '../schemas.ts';

type Approval = ApprovalListProps['approvals'][number];
type Step = AgentActivityProps['steps'][number];

/**
 * The requests, one a row: Agent or AI tool before what it would do (and what it's about), who asked
 * and when (on the company's clock, `timeZone`, never the device's: QA-939) and why under it, and why
 * it isn't this person's to decide. With `ends`, whether it waits for them or for someone else at its
 * end (a chat leaves that out).
 */
export const approvalRows = (
  approvals: readonly Approval[],
  { timeZone, ends = true }: { timeZone: string; ends?: boolean },
): Row[] =>
  approvals.map((a) => {
    const what = Object.values(a.labels ?? {}).join(', ');
    return {
      id: a.id,
      kicker: a.agent.startsWith('user:') ? 'AI tool' : 'Agent',
      name: `${a.actionTitle}${what ? `: ${what}` : ''}`,
      lines: [
        // the time ends in a.m. or p.m., its own full stop
        {
          text: `${agentName(a.agent)}, ${clock(a.createdAt, timeZone)}${a.explanation ? ` ${a.explanation}` : ''}`,
        },
        ...(a.cannotDecide ? [{ text: a.cannotDecide }] : []),
      ],
      ...(ends
        ? {
            end: a.cannotDecide
              ? { word: 'Someone else decides', tone: 'muted' as const }
              : { word: 'Waiting for you', tone: 'wait' as const },
          }
        : {}),
    };
  });

const MODE: Record<Step['mode'], [string, RowTone]> = {
  automatic: ['Done', 'muted'],
  asked: ['Waiting for you', 'wait'],
  refused: ['Refused', 'bad'],
  failed: ['Failed', 'bad'],
};

const DECIDED = { approved: 'Approved', declined: 'Declined', expired: 'Expired' } as const;

/**
 * The agents' steps, one a row: what it did, which agent and when and how it went under it; at its
 * end how a person decided it, or Done, Waiting for you, Refused or Failed. The time is the company's
 * clock (`timeZone`), never the device's (QA-939).
 */
export const activityRows = (steps: readonly Step[], timeZone: string): Row[] =>
  steps.map((s) => ({
    id: `${s.runId}-${s.n}`,
    name: s.actionTitle ?? s.task,
    lines: [{ text: `${agentName(s.agentKey)}, ${clock(s.at, timeZone)} ${s.outcome}` }],
    end: s.decision
      ? {
          word: `${DECIDED[s.decision.status]}${s.decision.by && s.decision.status !== 'expired' ? ` by ${s.decision.by}` : ''}`,
          tone: s.decision.status === 'approved' ? 'muted' : 'bad',
        }
      : { word: MODE[s.mode][0], tone: MODE[s.mode][1] },
  }));
